import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import {
  getDefaultView,
  isValidView,
  getViewsForType,
  hasViewPermission,
} from '@/utils/viewHelpers';
import { ViewPaths, ViewConfig } from '@/types/views';
import Header from '@/components/Header/Header';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { HeaderMenu } from '@/components/Header/HeaderMenu';
import { ApplicationsList } from '@/components/Organizations/ApplicationsList';
import { OrganizationsList } from '@/components/Organizations/OrganizationsList';
import { FormattedMessage, useIntl } from 'react-intl';
import { DialogWindow } from '@/components/Dialog';
import { OrganizationCreateForm } from '@/components/Organizations/OrganizationCreateForm';
import { SearchFilterParams, SortDirection } from '@/types/filterSearch';
import { ActionButtons } from '@/components/Header/ActionButtons';
import Button from '@/components/core/Button';
import { IconPlus } from '@/icons';
import { ViewPermissions } from '@/components/Auth/ViewPermissions';
import {
  useListOrganizations,
  useListOrganizationApplications,
} from '@/api/trafikportalen';
import { Loading } from '@/components/Loading';
import { FilteredListContainer } from '@/components/FilteredListContainer';
import { DocumentHead } from '@/components/core/DocumentHead';
import { useAuth } from '@/stores/auth';
import { useQueryClient } from '@tanstack/react-query';

export const Route = createFileRoute('/$tenantId/_layout/organizations/$view')({
  component: BeforeLoad,
  validateSearch: (search: Record<string, unknown>): SearchFilterParams => {
    return {
      query: search.query as string,
      filter: search.filter as string,
      studentAccess: search.studentAccess as string,
      sort: search.sort as string,
      direction: search.direction as SortDirection,
    };
  },
  beforeLoad: ({ params: { tenantId, view } }) => {
    if (view === 'periods') {
      throw redirect({
        to: '/$tenantId/periods',
        params: { tenantId },
      });
    }
    if (!isValidView(view, 'organizations')) {
      throw redirect({
        to: '/$tenantId/organizations/$view',
        params: {
          tenantId,
          view: getDefaultView('organizations'),
        },
      });
    }
  },
});

function BeforeLoad() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { tenantId, view } = Route.useParams();

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const { userProfile, setUserProfile, previousUserProfile } =
        useAuth.getState();
      if (userProfile?.expires_at && previousUserProfile) {
        await queryClient.cancelQueries();
        setUserProfile(previousUserProfile);
        window.location.reload();
      } else {
        if (!hasViewPermission(view, 'organizations', 'tenant:viewer')) {
          navigate({
            to: '/$tenantId',
            params: { tenantId },
            replace: true,
          });
        }
      }
      setLoading(false);
    };
    load();
  }, [navigate, queryClient, tenantId, view]);

  if (loading) {
    return <Loading />;
  }

  return <OrganizationsView />;
}

function OrganizationsView() {
  const { tenantId, view } = Route.useParams();
  const selected = view as ViewPaths<'organizations'>;
  const navigate = useNavigate();

  const [open, setOpen] = useState<boolean>(false);

  const { data: organizations, isLoading } = useListOrganizations();
  const { data: organizationApplications } = useListOrganizationApplications();

  const { formatMessage } = useIntl();

  const onViewChange = useCallback(
    (view?: string) => {
      if (!view) return;

      navigate({
        to: Route.to,
        params: { tenantId, view },
        replace: false,
      });
    },
    [navigate, tenantId],
  );

  const submittedApplicationsPath = useMemo(() => {
    if (!organizationApplications) return undefined;

    const count = organizationApplications.filter(
      (application) => application.status === 'submitted',
    ).length;

    if (count > 0) {
      return ['applications'];
    }
  }, [organizationApplications]);

  const views = useMemo(() => {
    return getViewsForType('organizations') as ViewConfig[];
  }, []);

  const metadataKeys = useMemo(() => {
    switch (selected) {
      case 'list':
        return {
          titleKey: 'page.organizations.list.title',
          descriptionKey: 'page.organizations.list.description',
        };
      case 'applications':
        return {
          titleKey: 'page.organizations.applications.title',
          descriptionKey: 'page.organizations.applications.description',
        };
      default:
        return {
          titleKey: 'page.organizations.title',
          descriptionKey: 'page.organizations.description',
        };
    }
  }, [selected]);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <>
      <DocumentHead
        titleKey={metadataKeys.titleKey}
        descriptionKey={metadataKeys.descriptionKey}
      />
      <Header
        title={formatMessage({ defaultMessage: 'Organisationer' })}
        menu={
          <HeaderMenu
            options={views}
            selected={selected}
            onViewChange={onViewChange}
            notificationViews={submittedApplicationsPath}
          />
        }
        actions={
          <ViewPermissions permission="tenant:editor">
            <ActionButtons>
              <Button
                startIcon={<IconPlus />}
                onClick={() => setOpen(true)}
                variant="outline"
              >
                <FormattedMessage defaultMessage="Lägg till organisation" />
              </Button>
            </ActionButtons>
          </ViewPermissions>
        }
      />
      <FilteredListContainer>
        {selected === 'list' && (
          <OrganizationsList organizations={organizations} />
        )}
        {selected === 'applications' && <ApplicationsList />}
      </FilteredListContainer>
      <DialogWindow
        title={formatMessage({
          defaultMessage: 'Lägg till organisation',
        })}
        content={<OrganizationCreateForm onOpenChange={setOpen} />}
        onOpenChange={setOpen}
        open={open}
      />
    </>
  );
}
